import { Button } from '@eventbrite/eds-button';
import { Icon } from '@eventbrite/eds-icon';
import { Calendar } from '@eventbrite/eds-iconography';
import React from 'react';
import { isEventEligibleForRepeatingEventsConsumerExperiment } from '../../../../statsig';
import type {
    CheckoutWidgetProps,
    Event,
    Organizer,
    RemindMeProps,
} from '../../../../types';
import { SALES_STATUS } from '../../../../types';
import RemindMe from '../../../RemindMe/RemindMe';
import { RSVPLabel, RSVPOptions } from '../../../RSVP/RSVP';
import { CheckoutModalButton } from '../CheckoutModalButton/CheckoutModalButton';
import { ExploreSimilarEventsButton } from '../ExploreSimilarEventsButton/ExploreSimilarEventsButton';
import './ConversionBar.scss';

export type ConversionBarCommonProps = {
    statusIsUnavailable: boolean;
    shouldDisplayRsvp: boolean;
    shouldShowStatus: boolean;
    panelDisplayPrice: string;
    statusToDisplay: string;
    shouldShowCheckoutButton: boolean;
    getSeriesDefaultOptionTerminology: string;
    ticketModalButton: any;
    shouldShowRemindMeButton: boolean;
    shouldShowTurnOffReminderButton: boolean;
    isExpiredEvent: boolean;
    salesStatus: string;
    isSalesEndedListingExperimentEnabled: boolean;
    handleExploreSimilareEventsButtonClick: Function;
    hasEarlyBird?: boolean;
};

export type ConversionBarComponentProps = ConversionBarCommonProps &
    CheckoutWidgetProps &
    RemindMeProps & {
        externalTickets?: {
            //TODO: build on the backend
            url: string;
            text: string;
            available: boolean;
            shouldShowExternalTicketingProvider: boolean;
            externalTicketingProviderLabelText?: string;
        };
    } & Pick<Event, 'isSeriesParent' | 'isPreview' | 'childEvents'> & {
        eventName?: Event['name'];
        organizerName?: Organizer['name'];
        organizerAvatar?: Organizer['profilePicture'];
    };

export const ConversionBar = ({
    affiliateCode,
    campaign_token,
    promoCode,
    rsvpToken,
    waitlistToken,
    statusIsUnavailable,
    shouldDisplayRsvp,
    shouldShowStatus,
    statusToDisplay,
    panelDisplayPrice,
    externalTickets,
    ticketModalButton,
    isPreview,
    shouldShowRemindMeButton,
    shouldShowCheckoutButton,
    isSeriesParent,
    getSeriesDefaultOptionTerminology,
    eventName,
    eventUrl,
    isRemindedEvent,
    organizerId,
    organizerName,
    organizerAvatar,
    isAuthenticated,
    salesStatus,
    isSalesEndedListingExperimentEnabled,
    handleExploreSimilareEventsButtonClick,
    childEvents,
}: ConversionBarComponentProps) => {
    const {
        shouldShowExternalTicketingProvider,
        externalTicketingProviderLabelText,
    } = externalTickets ?? {};

    const ExternalTickets = () => (
        <Button
            href={externalTickets?.url}
            type="link"
            style="fill"
            size="block"
            target="_blank"
            rel="noreferrer"
        >
            {externalTickets?.text}
        </Button>
    );

    const CheckoutButton: React.FC = ({ children }) =>
        isSalesEndedListingExperimentEnabled &&
        salesStatus === SALES_STATUS.SALES_ENDED ? (
            <ExploreSimilarEventsButton
                handleClick={handleExploreSimilareEventsButtonClick}
            />
        ) : (
            <CheckoutModalButton
                {...{
                    affiliateCode,
                    campaign_token,
                    children,
                    isPreview,
                    isSeriesParent,
                    promoCode,
                    rsvpToken,
                    shouldShowStatus,
                    ticketModalButton,
                    waitlistToken,
                    isSalesEndedListingExperimentEnabled,
                    childEvents,
                }}
            />
        );

    const CheckoutArea = () => (
        <>
            {shouldShowRemindMeButton && (
                <RemindMe
                    eventUrl={eventUrl}
                    eventName={eventName}
                    isRemindedEvent={isRemindedEvent}
                    organizerId={organizerId}
                    organizerName={organizerName}
                    organizerAvatar={organizerAvatar}
                    isAuthenticated={isAuthenticated}
                />
            )}
            <CheckoutButton />
        </>
    );

    const SingleEvent = () => (
        <>
            {shouldDisplayRsvp ? (
                <RSVPOptions
                    {...{
                        affiliateCode,
                        campaign_token,
                        isPreview,
                        isSeriesParent,
                        promoCode,
                        rsvpToken,
                        shouldShowStatus,
                        ticketModalButton,
                        waitlistToken,
                        childEvents,
                    }}
                />
            ) : (
                <>
                    {shouldShowCheckoutButton && <CheckoutArea />}
                    {externalTickets?.available && <ExternalTickets />}
                </>
            )}
        </>
    );

    const isParentEventExperimentVariant =
        isEventEligibleForRepeatingEventsConsumerExperiment(
            isSeriesParent,
            childEvents,
        );
    const SeriesParent = () => (
        <>
            {shouldShowStatus || isParentEventExperimentVariant ? (
                <CheckoutButton />
            ) : (
                <CheckoutButton>
                    <Icon type={<Calendar />} />
                    {getSeriesDefaultOptionTerminology}
                </CheckoutButton>
            )}
        </>
    );

    const PanelInfo = () => {
        const status = shouldShowStatus ? statusToDisplay : panelDisplayPrice;

        return (
            <div
                className="conversion-bar__panel-info"
                data-testid="panel-info"
            >
                {shouldDisplayRsvp ? (
                    <div id="listings-root__rsvp-label">
                        <RSVPLabel />
                    </div>
                ) : (
                    <>
                        {status}
                        {shouldShowExternalTicketingProvider && (
                            <>
                                {' '}
                                <span className="conversion-bar__additional-info">
                                    {externalTicketingProviderLabelText}
                                </span>
                            </>
                        )}
                    </>
                )}
            </div>
        );
    };

    const shouldShowCTA =
        !statusIsUnavailable &&
        (shouldShowCheckoutButton ||
            externalTickets?.available ||
            shouldDisplayRsvp);

    return (
        <div
            className="conversion-bar conversion-bar--checkout-opener"
            data-testid="conversion-bar"
        >
            <div className="conversion-bar__body">
                <PanelInfo />
            </div>
            {shouldShowCTA && (
                <div className="conversion-bar__cta-wrapper">
                    {isSeriesParent ? <SeriesParent /> : <SingleEvent />}
                </div>
            )}
        </div>
    );
};
